import { useAnalytics } from '@ecosystem/newron-client-lib';
import { useSelector } from 'react-redux';
import { getCountryFromSideFilters, getProductGroupFromSideFilters } from '@utils/FilterUtils';
import { PageName, PageType } from '@utils/pageConfig/Types';
import { concatenateSelectedFeatureName, doAmplitudeAnalyticsTracking } from '../PageAnalytics';
import {
  AnalyticTrackingBasePropertiesObj,
  FilterType,
  TrackingGeneralProp,
} from './AnalyticTrackingBasePropertiesObj';

//Example of tracking for - Top Page Filter
/**
 analytics.track("Filter Applied", {
 "access": "Retail Insights",
 "locationId": "Germany",
 "productGroup": "PTV", //"All" or "PTV, AIR CONDITIONER"
 "filterName": "Timeframe",
 "filterType": "Platform",
 "periodicity": "Weekly",
 "pageName": "Retailer Overview",
 "retailerDataFor": "xxxxx",
 "section": "Retail Intelligence",
 "subsection": "Retailer Overview"
 })
 **/

export const TOP_PAGE_FILTER_TRACKING_TITLE = 'Filter Applied';

//Extend base properties
export interface TopPageFiltersTracking extends AnalyticTrackingBasePropertiesObj {
  filterName: string;
  filterType: string;
  optionChosen?: string;
  periodicity?: string;
  focusPeriod?: string;
  comparisonPeriod?: string;
}

export enum TopPageFilterFilterName {
  TIMEFRAME = 'Time frame',
  COMPARISON_PERIOD = 'Comparison period',
  COMPARE_IN = 'Compare in',
}

export interface TopPageFiltersTrackingFunctionParam {
  eventTitle: string;
  sideFilters: any;
  retailerClientState: any;
  track: (event: string, properties?: Object) => void;
  pageType: PageType;
  pageName: PageName;
  filterName: TopPageFilterFilterName;
  filterType: FilterType;
  optionChosen?: string;
  periodicity?: string;
  focusPeriod?: string;
  comparisonPeriod?: string;
  access: Object;
}

export const topPageFiltersTrackingFunction = (param: TopPageFiltersTrackingFunctionParam) => {
  const {
    pageName,
    sideFilters,
    retailerClientState,
    track,
    eventTitle,
    pageType,
    filterName,
    filterType,
    optionChosen,
    periodicity,
    focusPeriod,
    comparisonPeriod,
    access,
  } = param;

  const selectedSideFilter = sideFilters[pageType];

  const appliedProductGroup = getProductGroupFromSideFilters(selectedSideFilter);
  const appliedFiltersCountry = getCountryFromSideFilters(selectedSideFilter);

  const topPageFiltersProperties: TopPageFiltersTracking = {
    access: access,
    locationId: appliedFiltersCountry?.options.map((options) => options.label),
    itemGroup: concatenateSelectedFeatureName(appliedProductGroup),
    pageName,
    retailerDataFor: retailerClientState.clientName,
    section: TrackingGeneralProp.SECTION_TRACKING_PROP,
    subsection: pageName,
    filterName,
    filterType,
    optionChosen,
    periodicity,
    focusPeriod,
    comparisonPeriod,
  };

  doAmplitudeAnalyticsTracking(eventTitle, track, topPageFiltersProperties);
};

export const useGenerateTopPageFiltersTrackingParam = (pageName: PageName, pageType: PageType) => {
  const { track } = useAnalytics();
  const sideFiltersState = useSelector((state: any) => state.sideFilters);
  const retailerClientState = useSelector((state: any) => state.retailerClient);

  return {
    pageName,
    sideFilters: sideFiltersState.appliedFilters,
    retailerClientState: retailerClientState,
    track,
    eventTitle: TOP_PAGE_FILTER_TRACKING_TITLE,
    pageType,
    filterName: TopPageFilterFilterName.TIMEFRAME,
    filterType: FilterType.SELECTORS,
  };
};
